/**
 * App Context - Global Application State
 *
 * Manages global state using useReducer pattern:
 * - Reports list
 * - Current selected report
 * - localStorage persistence
 */
import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  type ReactNode,
  type Dispatch,
} from 'react';
import type { Report } from '../types';

// State shape
interface AppState {
  reports: Report[];
  currentReport: Report | null;
  currentReportId: string | null;
}

// Action types
type AppAction =
  | { type: 'SET_REPORTS'; payload: Report[] }
  | { type: 'SET_CURRENT_REPORT'; payload: Report | null }
  | { type: 'SET_CURRENT_REPORT_BY_ID'; payload: string }
  | { type: 'CLEAR_STATE' };

// Initial state
const initialState: AppState = {
  reports: [],
  currentReport: null,
  currentReportId: null,
};

// Storage key for persistence
const STORAGE_KEY = 'pbi-portal-app-state';

/**
 * Load persisted state from localStorage
 */
const loadPersistedState = (): Partial<AppState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        currentReportId: parsed.currentReportId || null,
      };
    }
  } catch (error) {
    console.warn('Failed to load persisted state:', error);
  }
  return {};
};

/**
 * Save state to localStorage
 */
const persistState = (state: AppState) => {
  try {
    const toStore = {
      currentReportId: state.currentReportId,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    console.warn('Failed to persist state:', error);
  }
};

/**
 * App reducer
 */
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_REPORTS': {
      const reports = action.payload;
      // If we have a persisted currentReportId, try to restore it
      let currentReport = state.currentReport;
      if (state.currentReportId && !currentReport) {
        currentReport = reports.find(r => r.id === state.currentReportId) || null;
      }
      // Do NOT auto-select the first report - let user choose explicitly
      return {
        ...state,
        reports,
        currentReport,
        currentReportId: currentReport?.id || null,
      };
    }

    case 'SET_CURRENT_REPORT': {
      const report = action.payload;
      return {
        ...state,
        currentReport: report,
        currentReportId: report?.id || null,
      };
    }

    case 'SET_CURRENT_REPORT_BY_ID': {
      const reportId = action.payload;
      const report = state.reports.find(r => r.id === reportId) || null;
      return {
        ...state,
        currentReport: report,
        currentReportId: reportId,
      };
    }

    case 'CLEAR_STATE':
      localStorage.removeItem(STORAGE_KEY);
      return initialState;

    default:
      return state;
  }
};

// Context types
interface AppContextType {
  state: AppState;
  dispatch: Dispatch<AppAction>;
  // Convenience methods
  setReports: (reports: Report[]) => void;
  setCurrentReport: (report: Report | null) => void;
  selectReportById: (id: string) => void;
  clearState: () => void;
}

const AppContext = createContext<AppContextType | null>(null);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  // Initialize with persisted state
  const [state, dispatch] = useReducer(appReducer, {
    ...initialState,
    ...loadPersistedState(),
  });

  // Persist state changes
  useEffect(() => {
    persistState(state);
  }, [state.currentReportId]);

  // Convenience methods
  const setReports = (reports: Report[]) => {
    dispatch({ type: 'SET_REPORTS', payload: reports });
  };

  const setCurrentReport = (report: Report | null) => {
    dispatch({ type: 'SET_CURRENT_REPORT', payload: report });
  };

  const selectReportById = (id: string) => {
    dispatch({ type: 'SET_CURRENT_REPORT_BY_ID', payload: id });
  };

  const clearState = () => {
    dispatch({ type: 'CLEAR_STATE' });
  };

  const value: AppContextType = {
    state,
    dispatch,
    setReports,
    setCurrentReport,
    selectReportById,
    clearState,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

/**
 * Hook to use app context
 */
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
};
